import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import GlobalStyles from '@mui/material/GlobalStyles';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { alpha, useTheme } from '@mui/material/styles';
import {
  CheckmarkCircleIcon,
  CloseIcon,
  ErrorCircleIcon,
  InfoCircleIcon,
  WarningIcon,
} from '@tyro/icons';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import { Toaster as SonnerToaster } from 'sonner';

export { toast } from 'sonner';

type IconType = ForwardRefExoticComponent<
  Omit<SvgIconProps, 'ref'> & RefAttributes<SVGSVGElement>
>;

type ToastIconProps = {
  Icon: IconType;
  color: 'info' | 'success' | 'warning' | 'error';
};

function ToastIcon({ Icon, color }: ToastIconProps) {
  return (
    <Box
      component="span"
      sx={{
        mr: 1.5,
        width: 40,
        height: 40,
        display: 'flex',
        borderRadius: 1.5,
        alignItems: 'center',
        justifyContent: 'center',
        color: `${color}.main`,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.16),
      }}
    >
      <Icon sx={{ width: 24 }} />
    </Box>
  );
}

export function Toaster() {
  const theme = useTheme();
  return (
    <>
      <SonnerToaster
        position="top-right"
        icons={{
          info: <ToastIcon Icon={InfoCircleIcon} color="info" />,
          success: <ToastIcon Icon={CheckmarkCircleIcon} color="success" />,
          warning: <ToastIcon Icon={WarningIcon} color="warning" />,
          error: <ToastIcon Icon={ErrorCircleIcon} color="error" />,
          loading: (
            <Box
              sx={{
                width: 30,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress color="info" size={26} />
            </Box>
          ),
          close: <CloseIcon />,
        }}
        closeButton
      />
      <GlobalStyles
        styles={{
          '#root': {
            '[data-sonner-toaster]': {
              fontFamily: theme.typography.fontFamily,

              '&[data-sonner-theme=light]': {
                '--normal-text': theme.palette.text.primary,
              },
            },
            '[data-sonner-toast]': {
              padding: '9px 12px',
              borderRadius: 20,

              '&:has([data-button=true]) [data-close-button=true]': {
                display: 'none',
              },

              '&:has([data-close-button=true]) [data-content]': {
                paddingRight: 24,
              },

              '& [data-close-button="true"]': {
                background: 'transparent',
                border: 'none',
                width: 28,
                height: 28,
                top: '50%',
                right: 12,
                left: 'auto',
                transform: 'translateY(-50%)',

                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },

                '& > svg': {
                  width: 24,
                  height: 24,
                },
              },

              '&[data-type=loading] [data-icon]': {
                height: 30,
                width: 30,
              },

              '& [data-icon]': {
                height: 40,
                width: 40,
              },
            },
          },
        }}
      />
    </>
  );
}
